// src/lib/rbac/roles.ts

/**
 * All role types in the platform. Mirrors the `roleTypeEnum` in
 * @elixaa/db/schema/userRoles.ts and the keys of ROLE_PERMISSIONS in
 * elixaa-api/src/configs/permissionsConfig.ts.
 *
 * Per a690359: `lead_psychologist` is removed (lead is now a per-project
 * `isLead` flag on PractitionerAssignmentProject), `org_user` is added
 * (read-only analytics for attached locations within a single org).
 */
export const ROLE_TYPES = [
  'super_admin',
  'ba_super_admin',
  'ba_admin',
  'chief_psychologist',
  'psychologist',
  'volunteer',
  'customer_support',
  'org_user',
  'client',
] as const;

export type RoleType = (typeof ROLE_TYPES)[number];

/** Attached-to-user role record as returned by `GET /auth/me`
 *  (`{ id, role } | null`). One role per user — the array shape used in
 *  earlier FE versions is gone. See a690359 §4.1. */
export interface UserRole {
  id: string;
  role: RoleType;
}

/** Staff = everyone who works on the platform (not clients). org_user
 *  is BA-side org staff (not clinical) — included so login/logout
 *  destinations route them to the staff portal. Volunteers handle PHI
 *  in clinical sessions (notes, case reports, red flags), so the BE
 *  classes them as staff for MFA + staff-gated checks; FE mirrors that. */
export const STAFF_ROLES: readonly RoleType[] = [
  'super_admin',
  'ba_super_admin',
  'ba_admin',
  'chief_psychologist',
  'psychologist',
  'volunteer',
  'customer_support',
  'org_user',
] as const;

/** Clinical staff only. Mirrors elixaa-core's
 *  BUSINESS_ASSOCIATE_CLINICAL_ROLES — volunteers are clinical practitioners
 *  with the same write surface as psychologist (own clients, sessions,
 *  notes, case reports), minus engagement and case-report review. */
export const CLINICAL_ROLES: readonly RoleType[] = [
  'chief_psychologist',
  'psychologist',
  'volunteer',
] as const;

/** Admin staff only (the audience of this foundation spec). */
export const ADMIN_ROLES: readonly RoleType[] = [
  'super_admin',
  'ba_super_admin',
  'ba_admin',
] as const;

/** Admin role priority for login redirect (higher index = higher privilege). */
export const ADMIN_ROLE_PRIORITY: readonly RoleType[] = [
  'ba_admin',
  'ba_super_admin',
  'super_admin',
] as const;
